/**
 * Error handler utility for message API operations
 * Provides parsing, retry logic, and user-friendly error messages
 */

import type {
  MessageApiError,
  MessageApiErrorCode,
  MessageApiErrorType,
} from "./error-types";
import {
  isNetworkError,
  isAuthError,
  isValidationError,
  isNotFoundError,
  isRetryableError,
  isAuthError as checkIsAuthError,
} from "./error-types";
import { parseResponseError } from "./error-parser";

/**
 * Parse an error from fetch or other sources into a typed MessageApiError
 */
export function parseApiError(error: unknown): MessageApiError {
  if (error instanceof Response) {
    return parseResponseError(error);
  }

  if (error instanceof Error) {
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      return {
        type: "NetworkError",
        code: "NETWORK_ERROR",
        message: error.message,
        userMessage: "Unable to connect to the server. Please check your internet connection.",
        shouldRetry: true,
        retryAfterMs: 1000,
        originalError: error,
      };
    }

    if (error.name === "AbortError" || error.message.includes("timeout")) {
      return {
        type: "NetworkError",
        code: "TIMEOUT",
        message: error.message,
        userMessage: "Request timed out. Please try again.",
        shouldRetry: true,
        retryAfterMs: 2000,
        originalError: error,
      };
    }

    if (error.message.includes("validation") || error.message.includes("schema")) {
      return {
        type: "ValidationError",
        code: "VALIDATION_ERROR",
        message: error.message,
        userMessage: "Invalid data received from server. Please try again.",
        shouldRetry: false,
        originalError: error,
      };
    }

    return {
      type: "UnknownError",
      code: "UNKNOWN_ERROR",
      message: error.message,
      userMessage: "An unexpected error occurred. Please try again.",
      shouldRetry: false,
      originalError: error,
    };
  }

  if (typeof error === "string") {
    return {
      type: "UnknownError",
      code: "UNKNOWN_ERROR",
      message: error,
      userMessage: "An unexpected error occurred. Please try again.",
      shouldRetry: false,
    };
  }

  return {
    type: "UnknownError",
    code: "UNKNOWN_ERROR",
    message: "Unknown error occurred",
    userMessage: "An unexpected error occurred. Please try again.",
    shouldRetry: false,
  };
}

export function shouldRetry(error: MessageApiError): boolean {
  return error.shouldRetry;
}

export function getErrorMessage(error: MessageApiError): string {
  return error.userMessage;
}

export function getErrorCode(error: MessageApiError): MessageApiErrorCode {
  return error.code;
}

export function getErrorType(error: MessageApiError): MessageApiErrorType {
  return error.type;
}

export interface ErrorRecoveryAction {
  type: "retry" | "reauth" | "contact_support" | "refresh" | "none";
  label: string;
  description: string;
}

export function getErrorRecoveryAction(error: MessageApiError): ErrorRecoveryAction {
  if (checkIsAuthError(error)) {
    return {
      type: "reauth",
      label: "Log In Again",
      description: "Your session has expired. Please log in again to continue.",
    };
  }

  if (isNotFoundError(error)) {
    return {
      type: "refresh",
      label: "Refresh",
      description: "The item may have been deleted. Refresh to see current data.",
    };
  }

  if (isRetryableError(error)) {
    return {
      type: "retry",
      label: "Try Again",
      description: `Please try again in ${Math.ceil(error.retryAfterMs / 1000)} seconds.`,
    };
  }

  if (isValidationError(error)) {
    return {
      type: "none",
      label: "Check Input",
      description: "Please check your input and try again.",
    };
  }

  return {
    type: "contact_support",
    label: "Contact Support",
    description: "An unexpected error occurred. Please contact support if the problem persists.",
  };
}

export function getRetryDelay(error: MessageApiError): number {
  return isRetryableError(error) ? error.retryAfterMs : 0;
}

export function requiresReauth(error: MessageApiError): boolean {
  return (
    checkIsAuthError(error) &&
    "requiresReauth" in error &&
    (error as { requiresReauth?: boolean }).requiresReauth === true
  );
}

export function formatErrorForLogging(error: MessageApiError): Record<string, unknown> {
  return {
    type: error.type,
    code: error.code,
    message: error.message,
    statusCode: error.statusCode,
    shouldRetry: error.shouldRetry,
    retryAfterMs: error.retryAfterMs,
    timestamp: new Date().toISOString(),
  };
}

export function createContextualError(
  baseError: MessageApiError,
  context: Record<string, unknown>
): MessageApiError & { context: Record<string, unknown> } {
  return {
    ...baseError,
    context,
  };
}
